import { useState, useCallback, useEffect, useRef } from 'react';
import { fetchCategories } from '../../../api/condition-suggestions.api';
import type { ConditionSuggestionCategory } from '../../../api/condition-suggestions.api';
import { CategoriesTab } from './CategoriesTab';
import { SuggestionsTab } from './SuggestionsTab';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Tag } from 'lucide-react';

// ─── i18n strings ─────────────────────────────────────────────────────────────
// No i18n library is installed in this portal (plain React + Vite, no
// react-i18next or similar). Strings are inlined here for now.
// All translatable labels are collected in this single object so they can be
// extracted into a proper locale file when i18n support is added.
// DE strings are used as the primary language to match the rest of the portal.
// The keys mirror the locale file spec from the Phase-3 plan (F3.5):
//   condition_suggestions.tabs.*   — Tab labels
//   condition_suggestions.modal.*  — Modal type-badge labels
//
// EN equivalents are commented next to each DE value.

const LABELS = {
  page: {
    /** Page heading — "Suggestion Library" */
    title: 'Vorschlags-Bibliothek',                // EN: "Suggestion Library"
    /** Page sub-heading */
    subtitle: 'Plattform-Bibliothek für Pre-Condition- und Behavior-Formulierungen',
    // EN: "Platform library for pre-condition and behavior formulations"
  },
  tabs: {
    /** condition_suggestions.tabs.pre_conditions */
    pre_conditions: 'Pre-Conditions',              // EN: "Pre-Conditions"  (DE same)
    /** condition_suggestions.tabs.behavior_instructions */
    behavior_instructions: 'Behavior Instructions', // EN: "Behavior Instructions" (DE same)
    /** condition_suggestions.tabs.categories */
    categories: 'Kategorien',                      // EN: "Categories"
  },
  modal: {
    /** condition_suggestions.modal.kind_label */
    kind_label: 'Typ',                             // EN: "Type"
    /** condition_suggestions.modal.kind_pre_condition */
    kind_pre_condition: 'Voraussetzung',            // EN: "Pre-Condition"
    /** condition_suggestions.modal.kind_behavior */
    kind_behavior: 'Verhaltensregel',              // EN: "Behavior Instruction"
  },
} as const;

// Export labels so SuggestionsTab and SuggestionFormModal can import them
// instead of duplicating strings, until a proper i18n solution is added.
export { LABELS as CONDITION_SUGGESTIONS_LABELS };

// ─── Tab type ─────────────────────────────────────────────────────────────────

/** The three top-level tabs on this page. */
type TabValue = 'pre-conditions' | 'behavior-instructions' | 'categories';

// ─── Minimal internal query state ────────────────────────────────────────────

interface AsyncState<T> {
  data: T | undefined;
  isLoading: boolean;
  error: Error | null;
}

function useDataLoader<T>(loader: () => Promise<T>): [AsyncState<T>, () => void] {
  const [state, setState] = useState<AsyncState<T>>({
    data: undefined,
    isLoading: true,
    error: null,
  });
  const loaderRef = useRef(loader);
  loaderRef.current = loader;
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, isLoading: true, error: null }));
    loaderRef
      .current()
      .then((data) => {
        if (!cancelled) setState({ data, isLoading: false, error: null });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({
            data: undefined,
            isLoading: false,
            error: err instanceof Error ? err : new Error(String(err)),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const refresh = useCallback(() => setTick((t) => t + 1), []);
  return [state, refresh];
}

// ─── Page ─────────────────────────────────────────────────────────────────────

/**
 * Admin page for managing the platform-wide suggestion library.
 *
 * Three tabs:
 * 1. Pre-Conditions  — CRUD for suggestions with kind=PRE_CONDITION
 * 2. Behavior Instructions — CRUD for suggestions with kind=BEHAVIOR_INSTRUCTION
 * 3. Categories — kind-agnostic category management
 */
export function ConditionSuggestionsPage() {
  // ─── Active tab (default: pre-conditions — most common use-case) ───────────
  const [activeTab, setActiveTab] = useState<TabValue>('pre-conditions');

  // ─── Shared categories (used by both SuggestionTab instances and CategoriesTab)
  const [categoriesState, refreshCategories] = useDataLoader<ConditionSuggestionCategory[]>(
    fetchCategories,
  );

  const handleCategoryRefresh = useCallback(() => {
    refreshCategories();
  }, [refreshCategories]);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
          <Tag className="w-5 h-5 text-indigo-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{LABELS.page.title}</h1>
          <p className="text-gray-600 text-sm">{LABELS.page.subtitle}</p>
        </div>
      </div>

      {/* Three-tab layout */}
      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as TabValue)}
        className="space-y-4"
      >
        <TabsList>
          <TabsTrigger value="pre-conditions">
            {LABELS.tabs.pre_conditions}
          </TabsTrigger>
          <TabsTrigger value="behavior-instructions">
            {LABELS.tabs.behavior_instructions}
          </TabsTrigger>
          <TabsTrigger value="categories">
            {LABELS.tabs.categories}
          </TabsTrigger>
        </TabsList>

        {/* Tab 1: Pre-Conditions */}
        <TabsContent value="pre-conditions">
          <SuggestionsTab
            kind="PRE_CONDITION"
            categories={categoriesState.data ?? []}
            onRefresh={handleCategoryRefresh}
          />
        </TabsContent>

        {/* Tab 2: Behavior Instructions */}
        <TabsContent value="behavior-instructions">
          <SuggestionsTab
            kind="BEHAVIOR_INSTRUCTION"
            categories={categoriesState.data ?? []}
            onRefresh={handleCategoryRefresh}
          />
        </TabsContent>

        {/* Tab 3: Categories (kind-agnostic, unchanged) */}
        <TabsContent value="categories">
          <CategoriesTab
            categories={categoriesState.data ?? []}
            isLoading={categoriesState.isLoading}
            error={categoriesState.error}
            onRefresh={handleCategoryRefresh}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
